import {useContext, useEffect, useState} from 'react';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {JSONContent} from '@tiptap/react';
import {AdditionalEvent} from '@/api/dto/care-journey';
import {Message, MessageThread, MessageThreadScope} from '@/api/dto/message';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {useAuthStore} from '@/store/auth_store';
import {GENERICERROR} from '@manual_encounters/constants/BannerMessages';
import Helpers from '@/helpers/helpers';

type Options = {
  event: AdditionalEvent | null;
  clientId: string;
  isOpen: boolean;
};

type Result = {
  notes: Message[];
  addNote: (content: JSONContent) => void;
  isThreadLoading: boolean;
  isThreadError: boolean;
};

// Notes data + add logic for an additional event, backed by the messaging thread.
//
// publishChatMessage is fire-and-forget over STOMP, so we never refetch right after a send — an
// immediate getMessageThread can race the write. localThread covers the thread-less first-note
// case until the real thread id shows up on the next open.
export function useAdditionalEventNotes({event, clientId, isOpen}: Options): Result {
  const {carecoApi, webSocketApi} = useContext(CarecoApiContext);
  const {currentUser} = useAuthStore();
  const queryClient = useQueryClient();

  const [localThread, setLocalThread] = useState<MessageThread | undefined>(undefined);

  // The modal is always mounted, so reset on event change too — switching events must not bleed the
  // previous event's optimistic localThread. localThread is transient per open event.
  useEffect(() => {
    setLocalThread(undefined);
  }, [isOpen, event?.id]);

  const {
    data: messageThread,
    isLoading: isThreadQueryLoading,
    isError: isThreadQueryError,
  } = useQuery({
    queryKey: ['messageThreads', event?.noteThreadId],
    queryFn: () => carecoApi?.getMessageThread(event!.noteThreadId!),
    enabled: !!carecoApi && isOpen && event != null && event.noteThreadId != null,
    refetchOnWindowFocus: false,
  });

  // The query is only run for events that already have a thread (noteThreadId != null). When it's
  // disabled there's no fetch, so loading/error must read false regardless of what react-query
  // reports for a disabled query — hence the explicit noteThreadId gate.
  const isThreadLoading = event?.noteThreadId != null && isThreadQueryLoading;
  const isThreadError = event?.noteThreadId != null && isThreadQueryError;

  const displayedThread = event?.noteThreadId != null ? messageThread : localThread;

  // Stored oldest-first; display newest-first.
  const notes: Message[] = displayedThread ? [...displayedThread.messages].reverse() : [];

  const addNote = (content: JSONContent) => {
    if (!event) {
      return;
    }
    if (!currentUser) {
      return;
    }

    let optimisticId: number | null = null;

    if (event.noteThreadId != null) {
      queryClient.setQueryData<MessageThread>(
        ['messageThreads', event.noteThreadId],
        (thread) => {
          if (!thread) return thread;
          optimisticId = -thread.messages.filter((message) => message.id < 0).length - 1;
          const optimisticMessage: Message = {
            id: optimisticId,
            user: currentUser,
            content,
            attachments: [],
            createdAt: new Date().toISOString(),
          };
          return {...thread, messages: [...thread.messages, optimisticMessage]};
        },
      );
    } else {
      setLocalThread((thread) => {
        optimisticId = -((thread?.messages.length ?? 0) + 1);
        const optimisticMessage: Message = {
          id: optimisticId,
          user: currentUser,
          content,
          attachments: [],
          createdAt: new Date().toISOString(),
        };
        if (!thread) {
          return {
            id: -1,
            clientId,
            scope: MessageThreadScope.INTERNAL,
            subject: null,
            messages: [optimisticMessage],
          };
        }
        return {...thread, messages: [...thread.messages, optimisticMessage]};
      });
    }

    // publishChatMessage throws synchronously when the STOMP client is disconnected; roll back the
    // optimistic note and surface the failure the same way MessageThreadDrawer does.
    try {
      webSocketApi?.publishChatMessage(
        event.noteThreadId ?? null,
        clientId,
        null,
        null,
        null,
        event.id,
        [],
        MessageThreadScope.INTERNAL,
        null,
        content,
      );
    } catch (error) {
      if (event.noteThreadId != null) {
        queryClient.setQueryData<MessageThread>(
          ['messageThreads', event.noteThreadId],
          (thread) => {
            if (!thread) return thread;
            return {...thread, messages: thread.messages.filter((message) => message.id !== optimisticId)};
          },
        );
      } else {
        setLocalThread((thread) => {
          if (!thread) return thread;
          const messages = thread.messages.filter((message) => message.id !== optimisticId);
          // Drop the thread entirely once the only (optimistic) note is rolled back, matching
          // MessageThreadDrawer clearing its temp thread on a publish failure.
          return messages.length === 0 ? undefined : {...thread, messages};
        });
      }

      Helpers.alertOnError(`Failed to add note. ${GENERICERROR}`, error as Error);
    }
  };

  return {notes, addNote, isThreadLoading, isThreadError};
}
